import { create } from 'zustand';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
  action?: { label: string; onClick: () => void };
}

interface ToastState {
  toasts: Toast[];
  add: (toast: Omit<Toast, 'id'>) => void;
  dismiss: (id: string) => void;
}

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],

  add: (toast) => {
    const id = crypto.randomUUID();
    set((state) => ({
      toasts: [...state.toasts.slice(-2), { ...toast, id }], // max 3 visible
    }));

    // Auto-dismiss success/info after 4s; errors stay until dismissed
    if (toast.variant !== 'error') {
      setTimeout(() => {
        set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
      }, 4000);
    }
  },

  dismiss: (id) =>
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
}));

// Convenience helpers
function makeToast(message: string, variant: ToastVariant, action?: Toast['action']) {
  const payload: Omit<Toast, 'id'> = { message, variant };
  if (action) payload.action = action;
  useToastStore.getState().add(payload);
}

export const toast = {
  success: (message: string, action?: Toast['action']) => makeToast(message, 'success', action),
  error: (message: string, action?: Toast['action']) => makeToast(message, 'error', action),
  info: (message: string, action?: Toast['action']) => makeToast(message, 'info', action),
  warning: (message: string, action?: Toast['action']) => makeToast(message, 'warning', action),
};
